<template>
  <div class="relative w-full">
    <h6
      class="absolute z-10 max-w-full px-1 text-xs font-semibold text-theme-secondary left-4 -top-2 truncate backdrop-blur-sm"
    >
      {{ $t('What') }}
    </h6>

    <Autocomplete
      :key="listKey"
      v-model="selectedValue"
      class="text-theme-primary"
      :placeholder="$t('Job title, keyword..')"
      :options="options"
      v-bind="$attrs"
      drop-down-class="max-w-full bg-theme-paper"
      autocompleted="off"
      :callQuerySearch="handleQuerySearch"
      @change="handleTypedQuery"
      :selectedLabel="selectedLabel"
      itemValue="slug"
      itemLabel="label"
    />

    <svg
      class="absolute -translate-y-1/2 left-4 top-1/2"
      width="1.125rem"
      height="1.125rem"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M17.71 16.29L14.31 12.9C15.407 11.5025 16.0022 9.77666 16 8C16 6.41775 15.5308 4.87103 14.6518 3.55544C13.7727 2.23985 12.5233 1.21447 11.0615 0.608967C9.59966 0.00346625 7.99113 -0.15496 6.43928 0.153721C4.88743 0.462403 3.46197 1.22433 2.34315 2.34315C1.22433 3.46197 0.462403 4.88743 0.153721 6.43928C-0.15496 7.99113 0.00346625 9.59966 0.608967 11.0615C1.21447 12.5233 2.23985 13.7727 3.55544 14.6518C4.87103 15.5308 6.41775 16 8 16C9.77666 16.0022 11.5025 15.407 12.9 14.31L16.29 17.71C16.383 17.8037 16.4936 17.8781 16.6154 17.9289C16.7373 17.9797 16.868 18.0058 17 18.0058C17.132 18.0058 17.2627 17.9797 17.3846 17.9289C17.5064 17.8781 17.617 17.8037 17.71 17.71C17.8037 17.617 17.8781 17.5064 17.9289 17.3846C17.9797 17.2627 18.0058 17.132 18.0058 17C18.0058 16.868 17.9797 16.7373 17.9289 16.6154C17.8781 16.4936 17.8037 16.383 17.71 16.29ZM2 8C2 6.81332 2.3519 5.65328 3.01119 4.66658C3.67047 3.67989 4.60755 2.91085 5.7039 2.45673C6.80026 2.0026 8.00666 1.88378 9.17055 2.11529C10.3344 2.3468 11.4035 2.91825 12.2426 3.75736C13.0818 4.59648 13.6532 5.66558 13.8847 6.82946C14.1162 7.99335 13.9974 9.19975 13.5433 10.2961C13.0892 11.3925 12.3201 12.3295 11.3334 12.9888C10.3467 13.6481 9.18669 14 8 14C6.4087 14 4.88258 13.3679 3.75736 12.2426C2.63214 11.1174 2 9.5913 2 8Z"
        class="fill-theme-disabled"
      />
    </svg>
  </div>
</template>

<script setup>
// import Treeselect from "vue3-treeselect";
// import the styles
import 'vue3-treeselect/dist/vue3-treeselect.css';
import Autocomplete from '@/Components/Autocomplete/JobSearchAutocomplete.vue';
import { onMounted, ref, watch } from 'vue';
import { fetchReferenceData } from '@/Composables/use-reference-data/useReferenceData';

defineOptions({
  inheritAttrs: false,
});

const emit = defineEmits(['updateQuery', 'typedQuery']);

const props = defineProps({
  route: String,
});

const selectedValue = defineModel();
const options = ref();
const selectedLabel = ref('');

const listKey = ref(1);

/**
 * Report what is currently typed without touching the combobox model: Headless UI
 * relies on a `null` value to know that nothing is selected, and picks the
 * highlighted option on blur as soon as the value is anything else.
 */
const handleTypedQuery = (query) => {
  emit('typedQuery', query);
};
const handleQuerySearch = (query) => {
  selectedValue.value = query;
  selectedLabel.value = query;
  emit('updateQuery', query);
  listKey.value++;
};
const getSelectedLabel = () => {
  if (!selectedValue.value || !options.value?.length) {
    return;
  }

  const option = options.value.find((option) => option.slug == selectedValue.value);
  selectedLabel.value = option ? option.label : selectedValue.value;
};
const itemChange = (itemValue) => {
  if (itemValue && selectedValue.value !== itemValue) {
    selectedValue.value = itemValue;
  }
};
const getList = async () => {
  try {
    options.value = await fetchReferenceData(props.route);
    getSelectedLabel();
  } catch (error) {
    console.error('Error fetching category data:', error);
  }
};

onMounted(() => {
  getList();
});

watch(selectedValue, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    // getSelectedLabel();
  }
});
</script>
<style>
.vue-treeselect__control {
  padding: 0.625rem 0 0.625rem 2.8125rem !important;
  min-height: 3.375rem;
  border-color: var(--color-divider);
  font-size: 0.875rem;
  border-radius: 0.5rem;
}
.vue-treeselect--multi .vue-treeselect__input {
  outline: none;
  --tw-ring-color: transparent;
  color: var(--color-primary);
}

.vue-treeselect__value-container {
  display: flex;
  height: 100%;
  align-items: center;
  border-color: var(--color-divider);
}

.vue-treeselect__placeholder.vue-treeselect-helper-zoom-effect-off {
  color: var(--color-neutral-500);
}

.vue-treeselect:not(.vue-treeselect--disabled)
  .vue-treeselect__multi-value-item:not(.vue-treeselect__multi-value-item-disabled):hover
  .vue-treeselect__multi-value-item:not(.vue-treeselect__multi-value-item-new)
  .vue-treeselect__multi-value-item:not(.vue-treeselect__multi-value-item-new):hover,
.vue-treeselect__multi-value-item {
  cursor: pointer;
  background: color-mix(in srgb, var(--color-blue-800) 10%, transparent) /*var(--color-paper)*/ !important;
  color: var(--color-neutral-500) /* var(--color-info) */ !important;
}

.vue-treeselect__multi-value-item {
  cursor: pointer;
  background: color-mix(in srgb, var(--color-blue-800) 10%, transparent) /*var(--color-paper)*/ !important;
  color: var(--color-neutral-500) /* var(--color-info) */ !important;
}

.vue-treeselect__input {
  border: none !important;
}

[type='text']:focus {
  box-shadow: none !important;
}

[data-mode="dark"] .vue-treeselect__control {
  background-color: transparent !important;
  border-color: var(--color-dark-divider) !important;
}

[data-mode="dark"] .vue-treeselect__menu {
  background-color: var(--color-dark-paper) !important;
  border-color: var(--color-dark-divider) !important;
}

[data-mode="dark"] .vue-treeselect__option--highlight {
  background-color: var(--color-dark-border) !important;
}

[data-mode="dark"] .vue-treeselect__label,
[data-mode="dark"] .vue-treeselect__placeholder.vue-treeselect-helper-zoom-effect-off {
  color: var(--color-disabled) !important;
}

[data-mode="dark"] .vue-treeselect--multi .vue-treeselect__input {
  color: var(--color-white) !important;
}
</style>
